import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgpInput } from 'ng-primitives/input';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { SuiFormControlBase } from '@sensika/sui/shared';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';

@Component({
  selector: 'sui-input',
  standalone: true,
  imports: [NgpInput, FaIconComponent],
  templateUrl: './sui-input.component.html',
  styleUrl: './sui-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiInputComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiInputComponent),
    },
  ],
})
export class SuiInputComponent
  extends SuiFormControlBase<string | number>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-input-${++SuiInputComponent._idCounter}`;
  private _focused = false;

  @ViewChild('nativeInput') private nativeInput?: ElementRef<HTMLInputElement>;

  readonly controlType = 'input';

  protected readonly faCircleExclamation = faCircleExclamation;

  @Input() type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' =
    'text';
  @Input() placeholder = '';
  @Input() readonly = false;
  /**
   * Opt-in invalid state for hosts that validate outside Angular forms (the
   * date pickers' free-text field, WAP-1664). OR-ed with the form-control
   * state; painted through aria-invalid.
   */
  @Input() invalid = false;
  @Input() autocomplete = '';
  @Input() id = '';
  // Numeric constraints — forwarded to the native input (meaningful for
  // type="number"; ignored otherwise). null = attribute omitted.
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | string | null = null;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return !this.value();
  }

  get errorState(): boolean {
    return (
      this.invalid || !!(this.ngControl?.invalid && this.ngControl?.touched)
    );
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
  }

  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    // Match native number-input semantics: emit a number (or null when empty)
    // for type="number"; a plain string otherwise.
    this.setValue(
      this.type === 'number' ? (raw === '' ? null : Number(raw)) : raw,
    );
  }

  /** Programmatically focus the underlying input. */
  focus(): void {
    this.nativeInput?.nativeElement.focus();
  }

  /** Select all text in the underlying input. */
  select(): void {
    this.nativeInput?.nativeElement.select();
  }
}
